import React from "react";

/**
 * Shared layout primitives for the SDPD briefing.
 *
 * Deliberately mirrors the idiom used by the TrialFindings report so the two
 * shareable pages read as one system: slate ink, mono section numbers, thin
 * rules, generous vertical rhythm.
 */

export function Section({
  id,
  number,
  title,
  subtitle,
  children,
}: {
  id: string;
  number?: string;
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="pt-14 md:pt-20 scroll-mt-16 break-inside-avoid">
      <div className="flex items-baseline gap-3 mb-2">
        {number && (
          <span className="text-sm font-mono text-slate-400 tabular-nums shrink-0">
            {number}
          </span>
        )}
        <h2 className="text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">
          {title}
        </h2>
      </div>
      {subtitle && (
        <div className="text-slate-600 leading-relaxed max-w-3xl mb-6">{subtitle}</div>
      )}
      {children}
    </section>
  );
}

export function SubSection({
  number,
  title,
  subtitle,
  children,
}: {
  number?: string;
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="flex items-baseline gap-3 mb-2">
        {number && (
          <span className="text-xs font-mono text-slate-400 tabular-nums shrink-0">
            {number}
          </span>
        )}
        <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      </div>
      {subtitle && (
        <div className="text-slate-600 text-sm leading-relaxed max-w-3xl mb-4">
          {subtitle}
        </div>
      )}
      {children}
    </div>
  );
}

/** A pulled-out claim. Used sparingly, three or four times in the document. */
export function Pullquote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="my-6 border-l-4 border-blue-600 pl-5 py-1">
      <p className="text-lg md:text-xl font-medium text-slate-900 leading-snug max-w-3xl">
        {children}
      </p>
    </blockquote>
  );
}

export function Callout({
  tone = "neutral",
  title,
  children,
}: {
  tone?: "neutral" | "warning" | "good" | "legal";
  title?: string;
  children: React.ReactNode;
}) {
  const tones = {
    neutral: "bg-slate-50 border-slate-200",
    warning: "bg-amber-50 border-amber-200",
    good: "bg-emerald-50 border-emerald-200",
    legal: "bg-blue-50 border-blue-200",
  } as const;
  const titleTones = {
    neutral: "text-slate-700",
    warning: "text-amber-800",
    good: "text-emerald-800",
    legal: "text-blue-800",
  } as const;
  return (
    <div className={`rounded-lg border p-4 md:p-5 ${tones[tone]} break-inside-avoid`}>
      {title && (
        <div
          className={`text-[11px] uppercase tracking-wider font-bold mb-2 ${titleTones[tone]}`}
        >
          {title}
        </div>
      )}
      <div className="text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

/** Small caps label used above groups of cards and tables. */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">
      {children}
    </div>
  );
}

export function Card({
  title,
  children,
  tag,
}: {
  title: string;
  children: React.ReactNode;
  tag?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 break-inside-avoid">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <h4 className="text-sm font-semibold text-slate-900 leading-snug">{title}</h4>
        {tag && (
          <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
            {tag}
          </span>
        )}
      </div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

/** Source attribution line. Every figure on this page carries one. */
export function Source({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
      <span className="font-semibold uppercase tracking-wider">Source:</span> {children}
    </p>
  );
}

/** Simple bordered table with a sticky-free header. */
export function Table({
  head,
  children,
  dense,
  minWidth = 560,
}: {
  head: React.ReactNode[];
  children: React.ReactNode;
  dense?: boolean;
  /** Below this the table scrolls horizontally instead of squeezing columns.
   *  Raise it for wide tables; nine numeric columns need far more than 560. */
  minWidth?: number;
}) {
  return (
    <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
      <table className="w-full border-collapse text-sm" style={{ minWidth }}>
        <thead>
          <tr className="border-b-2 border-slate-300">
            {head.map((h, i) => (
              <th
                key={i}
                className={`text-left font-semibold text-slate-700 ${
                  dense ? "py-2" : "py-2.5"
                } pr-4 text-[11px] uppercase tracking-wider align-bottom`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Td({
  children,
  className = "",
  strong,
}: {
  children: React.ReactNode;
  className?: string;
  strong?: boolean;
}) {
  return (
    <td
      className={`py-2.5 pr-4 align-top leading-relaxed ${
        strong ? "font-semibold text-slate-900" : "text-slate-600"
      } ${className}`}
    >
      {children}
    </td>
  );
}

/** Yes / no / partial marker for comparison tables. Never color alone. */
export function Mark({ value }: { value: "yes" | "no" }) {
  if (value === "yes") {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-xs">
        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
            clipRule="evenodd"
          />
        </svg>
        Yes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-red-700 font-semibold text-xs">
      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <path
          fillRule="evenodd"
          d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
          clipRule="evenodd"
        />
      </svg>
      No
    </span>
  );
}

/** Numbered step in a process diagram. */
export function Step({
  n,
  title,
  children,
  color,
}: {
  n: number | string;
  title: string;
  children?: React.ReactNode;
  color?: string;
}) {
  return (
    <div className="flex gap-3">
      <div
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
        style={{ backgroundColor: color ?? "#1a78c2" }}
      >
        {n}
      </div>
      <div className="min-w-0 pt-0.5">
        <div className="text-sm font-semibold text-slate-900 leading-snug">{title}</div>
        {children && (
          <div className="text-xs text-slate-600 leading-relaxed mt-1">{children}</div>
        )}
      </div>
    </div>
  );
}
